import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { EASE, VENUE } from "@/lib/site";
import Countdown from "./Countdown";

const BASE = 2.1;

function Reveal({ children, delay, className = "", innerClassName = "" }) {
  return (
    <span className={`block overflow-hidden ${className}`}>
      <motion.span
        className={`block will-change-transform ${innerClassName}`}
        initial={{ y: "112%" }}
        animate={{ y: 0 }}
        transition={{ duration: 1.1, delay, ease: EASE }}
      >
        {children}
      </motion.span>
    </span>
  );
}

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const watermarkY = useTransform(scrollYProgress, [0, 1], [0, 180]);
  const metaY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <section id="top" ref={ref} data-testid="hero-section" className="relative overflow-hidden">
      <motion.span
        aria-hidden="true"
        style={{ y: watermarkY }}
        className="pointer-events-none absolute -bottom-[6vw] left-0 select-none font-serif text-[26vw] font-medium leading-none tracking-tighter text-transparent [-webkit-text-stroke:1.5px_rgba(88,11,43,0.3)]"
      >
        RECON
      </motion.span>

      <div className="relative mx-auto max-w-7xl px-6 pb-28 pt-36 md:px-10 md:pt-44">
        <Reveal delay={BASE}>
          <p className="font-sans text-[11px] uppercase tracking-[0.5em] text-wine">
            A WMWE Signature Gathering
          </p>
        </Reveal>

        <h1 className="mt-10 font-serif font-medium leading-[0.85] tracking-tight text-ink">
          <Reveal delay={BASE + 0.15} innerClassName="text-[19vw] md:text-[15vw]">
            RECON<span className="align-top text-[0.28em] tracking-normal text-oxblood">2026</span>
          </Reveal>
        </h1>

        <div className="mt-8 flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
          <div>
            <Reveal delay={BASE + 0.35}>
              <p className="font-sans text-sm tracking-[0.6em] text-ink md:text-base">
                THE&nbsp;BRIEFING
              </p>
            </Reveal>
            <Reveal delay={BASE + 0.5} className="mt-4">
              <p className="font-serif text-2xl italic text-ink/80 md:text-3xl">
                Before your next move, gather intelligence.
              </p>
            </Reveal>
          </div>

          <motion.div
            style={{ y: metaY }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: BASE + 0.6, duration: 0.9, ease: EASE }}
            className="border-l border-ink/20 pl-6 font-sans text-[11px] leading-relaxed tracking-[0.2em] text-ink/70"
          >
            <p>NOV 13, 2026 — 11:00 AM–3:00 PM</p>
            <p>NOV 14, 2026 — 7:00 PM–10:00 PM</p>
            <p className="mt-2 text-wine">
              {VENUE.name.toUpperCase()} — {VENUE.address.toUpperCase()}
            </p>
            <p className="text-wine">{VENUE.city.toUpperCase()}</p>
          </motion.div>
        </div>

        <motion.div
          className="mt-16 border-t border-ink/20 pt-10"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: BASE + 0.7, duration: 0.9, ease: EASE }}
        >
          <div className="mb-10">
            <Countdown />
          </div>
          <a
            href="/checkout"
            data-testid="hero-cta-button"
            className="group relative inline-flex items-center overflow-hidden border border-wine bg-wine px-10 py-5 font-sans text-xs tracking-[0.4em] text-bone"
          >
            <span className="absolute inset-0 translate-y-full bg-navy transition-transform duration-500 ease-out group-hover:translate-y-0" />
            <span className="relative">GET YOUR BRIEFING</span>
            <span className="relative ml-4 inline-block transition-transform duration-300 group-hover:translate-x-1.5">
              →
            </span>
          </a>
          <p className="mt-5 font-sans text-[10px] tracking-[0.3em] text-ink/50">
            YOUR NEXT MOVE DESERVES INTELLIGENCE.
          </p>
        </motion.div>
      </div>
    </section>
  );
}
